import { Link, styled, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

import { GLOBAL_API_ERROR_CODES } from '@data/global-api-errors';
import type { GlobalApiErrorCode } from '@data/global-api-errors';
import {
  LiveRegionAnnouncer,
  useTranslationsContext,
} from '@state/checkout-contexts';
import type { ChkInputGroupProps } from '@ui/checkout-components';
import {
  ChkIconEye,
  ChkIconEyeSlash,
  ChkInputGroup,
  ChkLogoWrapper,
} from '@ui/checkout-components';
import { UiButtonWithLoading } from '@ui/molecules-button-with-loading';
import { captureException, isNetworkError } from '@utility/checkout-utils';

import { ErrorAlert } from '../../components/ErrorAlert/ErrorAlert';
import { HeaderWithCloseButton } from '../../components/HeaderWithCloseButton/HeaderWithCloseButton';
import { ViewContainer } from '../../components/ViewContainer/ViewContainer';
import { ERROR_CODES_IN_ALERT, SCA_ERRORS } from '../../global.constants';
import { useCancelModal } from '../../hooks/useCancelModal/useCancelModal';
import { useLimitExceededRedirect } from '../../hooks/useLimitExceededRedirect';
import { SCA_EVENTS } from '../../models/globals/events.constants';
import { TEST_IDS } from '../../models/globals/testIds.constants';
import { useInstitutionDetailsContext } from '../../providers/InstitutionDetailsContextProvider/InstitutionDetailsContextProvider';
import { useScaSubject } from '../../providers/ScaSubjectProvider/ScaSubjectProvider.hooks';
import { dataLayer } from '../../utils/dataLayer';
import { getApiErrorCodeAndMessageKey } from '../../utils/getApiErrorCodeAndMessageKey';
import { getTraceIdAndTimestamp } from '../../utils/getTraceIdAndTimestamp';
import { isErrorHandled } from '../../utils/isErrorHandled';
import { TRANSLATION_KEYS } from '../../utils/TranslationKeys';

import {
  HANDLED_ERRORS,
  LOGIN_VIEW_CANCELLATION_REASONS,
  LOGIN_VIEW_NAME,
  LOGIN_VIEW_SELECT_DISABLED_REASON_ID,
} from './LoginView.constants';

export const LoginView = () => {
  const { t } = useTranslationsContext();
  const { logoUrl } = useInstitutionDetailsContext();

  const theme = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const wasPasswordRevealed = useRef(false);
  const { updateSubject, isFetching } = useScaSubject();
  const { modal, openModal } = useCancelModal({
    cancellationReasons: LOGIN_VIEW_CANCELLATION_REASONS,
    screenName: LOGIN_VIEW_NAME,
  });
  const [isInlineErrorVisible, setIsInlineErrorVisible] = useState(false);
  const [errorCode, setErrorCode] = useState<GlobalApiErrorCode | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const usernameInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const isLimitExceeded =
    errorCode === GLOBAL_API_ERROR_CODES.CODE_424.RETRY_LIMIT_EXCEEDED;
  const handleSubmitClick = async () => {
    dataLayer.trackEvent({ name: SCA_EVENTS.PUSHED_DATA });
    const { error } = await updateSubject({
      sca: {
        username,
        password,
      },
    });

    if (error !== null) {
      return handleError(error);
    }
  };

  const handleError = (error: unknown) => {
    const { code, messageKey, rawMessage } =
      getApiErrorCodeAndMessageKey(error);

    dataLayer.trackEvent({
      name: SCA_EVENTS.INCORRECT_DATA,
      properties: { incorrectDataType: SCA_ERRORS.CREDENTIALS },
    });
    dataLayer.trackEvent({
      name: SCA_EVENTS.LOGIN_PAGE_ERROR,
      properties: {
        code,
      },
    });

    setErrorCode(code as GlobalApiErrorCode);
    setErrorMessage(
      messageKey && rawMessage
        ? `${t(messageKey)}${getTraceIdAndTimestamp(rawMessage)}`
        : null
    );

    if (code === GLOBAL_API_ERROR_CODES.CODE_424.UNAUTHORISED) {
      setIsInlineErrorVisible(true);
      usernameInputRef.current?.focus();
    }

    if (!isNetworkError(error) && !isErrorHandled(code, HANDLED_ERRORS)) {
      captureException(error);
    }
  };

  useLimitExceededRedirect(isLimitExceeded);

  const handleRevealPasswordClick = useCallback(() => {
    setShowPassword((oldValue) => !oldValue);
    passwordInputRef.current?.focus();
  }, []);

  useEffect(() => {
    dataLayer.trackEvent({ name: SCA_EVENTS.LOGIN_PAGE_DISPLAYED });
  }, []);

  useEffect(() => {
    const isPasswordRevealed = showPassword === true;
    const isRevealedForTheFirstTime = wasPasswordRevealed.current === false;

    if (isPasswordRevealed && isRevealedForTheFirstTime) {
      wasPasswordRevealed.current = true;
      dataLayer.trackEvent({ name: SCA_EVENTS.PASSWORD_SHOWN });
    }
  }, [showPassword]);

  const userNameInputProps: ChkInputGroupProps = {
    label: t(TRANSLATION_KEYS.USER),
    type: 'text',
    testId: TEST_IDS.SCA_USERNAME_INPUT,
    value: username,
    onChange: (newValue: string) => setUsername(newValue),
    startAdornment: (
      <ChkLogoWrapper
        iconUrl={logoUrl}
        sx={{ width: '2.4rem', borderRadius: theme.borders.borderRadius1 }}
      />
    ),
    error: isInlineErrorVisible,
    errorText: (
      <Typography variant="body3" color={theme.coreTokens.states.error}>
        {t(TRANSLATION_KEYS.UNAUTHORISED_LOGIN)}
      </Typography>
    ),
    errorTextId: TEST_IDS.USERNAME_INPUT_ERROR_ID,
    fullWidth: true,
    inputRef: usernameInputRef,
  };

  const passwordInputProps: ChkInputGroupProps = {
    label: t(TRANSLATION_KEYS.PASSWORD),
    type: showPassword ? 'text' : 'password',
    testId: TEST_IDS.SCA_PASSWORD_INPUT,
    value: password,
    onChange: (newValue: string) => setPassword(newValue),
    endAdornment: (
      <Link
        component="button"
        sx={{
          padding: 0,
          background: 'transparent',
          height: '2.4rem',
          lineHeight: 1,
          color: 'inherit',
        }}
        onClick={handleRevealPasswordClick}
        data-testid={TEST_IDS.SCA_REVEAL_PASSWORD}
        aria-label={
          showPassword
            ? t(TRANSLATION_KEYS.HIDE_PASSWORD)
            : t(TRANSLATION_KEYS.SHOW_PASSWORD)
        }
      >
        {showPassword ? <ChkIconEyeSlash /> : <ChkIconEye />}
      </Link>
    ),
    error: isInlineErrorVisible,
    errorText: (
      <Typography variant="body3" color={theme.coreTokens.states.error}>
        {t(TRANSLATION_KEYS.UNAUTHORISED_LOGIN)}
      </Typography>
    ),
    errorTextId: TEST_IDS.PASSWORD_INPUT_ERROR_ID,
    fullWidth: true,
    inputRef: passwordInputRef,
  };

  const isAlertVisible = Boolean(
    errorCode &&
    ERROR_CODES_IN_ALERT.includes(errorCode as GlobalApiErrorCode) &&
    errorMessage
  );

  const isButtonDisabledWithSupportMessage = Boolean(!password || !username);

  const liveRegionSubmitMessage = isFetching
    ? t(TRANSLATION_KEYS.SUBMITTING_LOGIN)
    : null;

  return (
    <ViewContainer>
      <HeaderSection>
        <HeaderWithCloseButton
          onClick={openModal}
          aria-label={t(TRANSLATION_KEYS.OPEN_CANCEL_MODAL)}
          aria-haspopup={true}
        >
          {t(TRANSLATION_KEYS.LOG_IN)}
        </HeaderWithCloseButton>

        <Typography
          variant="body2"
          color={theme.coreTokens.text.muted}
          textAlign="center"
        >
          {t(TRANSLATION_KEYS.PLEASE_USE_SAME_LOGIN_DETAILS)}
        </Typography>
      </HeaderSection>
      <MainSection>
        <StyledBox>
          <ChkInputGroup {...userNameInputProps} />
          <ChkInputGroup {...passwordInputProps} />
        </StyledBox>

        <LiveRegionAnnouncer
          debounceDelay={500}
          message={liveRegionSubmitMessage}
        />
        <StyledButton
          variant="contained"
          onClick={handleSubmitClick}
          isLoading={isFetching}
          aria-disabled={isButtonDisabledWithSupportMessage}
          disabled={isLimitExceeded}
          data-testid={TEST_IDS.SCA_LOGIN_VIEW_CTA}
          aria-describedby={
            isButtonDisabledWithSupportMessage
              ? LOGIN_VIEW_SELECT_DISABLED_REASON_ID
              : undefined
          }
        >
          {t(TRANSLATION_KEYS.CONTINUE)}
        </StyledButton>
        <span style={visuallyHidden} id={LOGIN_VIEW_SELECT_DISABLED_REASON_ID}>
          {t(TRANSLATION_KEYS.PROVIDE_VALID_LOGIN_DETAILS_TO_CONTINUE)}
        </span>

        <ErrorAlert isVisible={isAlertVisible} message={errorMessage} />
      </MainSection>
      {modal}
    </ViewContainer>
  );
};

const StyledButton = styled(UiButtonWithLoading)(({ theme }) => ({
  marginTop: theme.spacing(9),
  marginBottom: theme.spacing(5),
}));

const StyledBox = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(6),
  marginTop: theme.spacing(6),
}));

const MainSection = styled('main')({ display: 'contents' });

const HeaderSection = styled('header')({ display: 'contents' });
